import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  getHistory, 
  getHistoryByDateRange,
  getLifetimeStats,
  downloadHistory,
  searchHistory,
  savePurification,
} from '../utils/historyManager';
import { getWalletHistory } from '../utils/supabaseManager';
import { CATEGORIES } from '../utils/toxicityCategories';
import './HistoryPanel.css';

const HistoryPanel = ({ isOpen, onClose, walletAddress = null, isDemoMode = false }) => {
  const [history, setHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedPurification, setSelectedPurification] = useState(null);
  const [dateFilter, setDateFilter] = useState('all'); // all, 7, 30
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedItems, setExpandedItems] = useState(new Set());
  const [isLoadingFromSupabase, setIsLoadingFromSupabase] = useState(false);

  // Load history on mount AND whenever panel opens
  useEffect(() => {
    loadHistory();
  }, []);

  useEffect(() => {
    if (isOpen) loadHistory();
  }, [isOpen]);

  // Update filtered history when filters change
  useEffect(() => {
    applyFilters();
  }, [history, dateFilter, searchQuery]);

  const loadHistory = async () => {
    // Demo mode — always use localStorage
    if (isDemoMode) {
      const allHistory = getHistory(null);
      const lifetimeStats = getLifetimeStats(null);
      setHistory(allHistory);
      setStats(lifetimeStats);
      return;
    }

    // Real wallet — show local data instantly, then sync from Supabase
    const localHistory = getHistory(walletAddress);
    if (localHistory.length > 0) {
      setHistory(localHistory);
      setStats(getLifetimeStats(walletAddress));
    }

    // Always sync from Supabase — handles cleared localStorage, new device, etc.
    if (walletAddress) {
      setIsLoadingFromSupabase(localHistory.length === 0);
      try {
        const supabaseHistory = await getWalletHistory(walletAddress);
        if (supabaseHistory.length > localHistory.length) {
          // Supabase has more — repopulate localStorage with missing entries
          supabaseHistory.forEach(entry => {
            const exists = localHistory.some(l => l.transactionHash && l.transactionHash === entry.transactionHash);
            if (!exists) savePurification(entry, walletAddress);
          });
          const refreshed = getHistory(walletAddress);
          setHistory(refreshed);
          setStats(getLifetimeStats(walletAddress));
        } else if (supabaseHistory.length > 0 && localHistory.length === 0) {
          // Local was empty (cleared) — restore from Supabase
          supabaseHistory.forEach(entry => savePurification(entry, walletAddress));
          const refreshed = getHistory(walletAddress);
          setHistory(refreshed);
          setStats(getLifetimeStats(walletAddress));
        }
      } catch (err) {
        console.error('Failed to sync history from Supabase:', err);
      } finally {
        setIsLoadingFromSupabase(false);
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...history];

    // Date filter
    if (dateFilter !== 'all') {
      const days = parseInt(dateFilter);
      filtered = isDemoMode ? getHistoryByDateRange(days, null) : getHistoryByDateRange(days, walletAddress);
    }

    // Search filter
    if (searchQuery) {
      filtered = searchHistory(searchQuery);
    }

    setFilteredHistory(filtered);
  };

  const toggleExpanded = (id) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedItems(newExpanded);
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const handleExport = (format) => {
    downloadHistory(format);
  };

  if (!isOpen) return null;

  return (
    <div className="history-panel-overlay">
      <motion.div
        className="history-panel"
        initial={{ x: '100%' }}
        animate={{ x: 0 }}
        exit={{ x: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
      >
        {/* Header */}
        <div className="history-header">
          <h2>📜 Purification History</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Filters */}
        <div className="history-filters">
          <select 
            value={dateFilter} 
            onChange={(e) => setDateFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Time</option>
            <option value="7">Last 7 Days</option>
            <option value="30">Last 30 Days</option>
          </select>

          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="export-buttons">
            <button 
              className="export-btn"
              onClick={() => handleExport('json')}
              title="Export as JSON"
            >
              📄 JSON
            </button>
            <button 
              className="export-btn"
              onClick={() => handleExport('csv')}
              title="Export as CSV"
            >
              📊 CSV
            </button>
          </div>
        </div>

        {/* Lifetime Stats Summary */}
        {stats && (
          <div className="lifetime-stats-summary">
            <div className="stat-card">
              <div className="stat-value">{stats.totalPurifications}</div>
              <div className="stat-label">Purifications</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalObjectsCleaned}</div>
              <div className="stat-label">Objects Cleaned</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{stats.totalPurityEarned.toLocaleString()}</div>
              <div className="stat-label">Purity Earned</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{
                (() => {
                  // Sum storageRebateMist directly - same source as per-entry display
                  const totalMist = history.reduce((sum, e) => {
                    if (e.storageRebateMist) return sum + Math.round(e.storageRebateMist);
                    return sum + Math.round((e.suiReclaimed || 0) * 1_000_000_000);
                  }, 0);
                  return totalMist.toLocaleString();
                })()
              }</div>
              <div className="stat-label">MIST Reclaimed</div>
            </div>
          </div>
        )}

        {/* History List */}
        <div className="history-list">
          {isLoadingFromSupabase ? (
            <div className="empty-history">
              <div className="empty-icon">⏳</div>
              <p>Loading history...</p>
              <p className="empty-subtitle">Syncing from chain</p>
            </div>
          ) : filteredHistory.length === 0 ? (
            <div className="empty-history">
              <div className="empty-icon">📜</div>
              <p>No purification history yet</p>
              <p className="empty-subtitle">
                {searchQuery ? 'No results found' : 'Start cleaning to build your history!'}
              </p>
            </div>
          ) : (
            filteredHistory.map((entry, index) => {
              const isExpanded = expandedItems.has(entry.id);
              const showDateHeader = index === 0 || 
                formatDate(entry.timestamp) !== formatDate(filteredHistory[index - 1]?.timestamp);

              return (
                <React.Fragment key={entry.id}>
                  {/* Date Header */}
                  {showDateHeader && (
                    <div className="date-header">
                      🗓️ {formatDate(entry.timestamp)}
                    </div>
                  )}

                  {/* History Entry */}
                  <motion.div
                    className="history-entry"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div 
                      className="history-entry-summary"
                      onClick={() => toggleExpanded(entry.id)}
                    >
                      <div className="entry-time">{formatTime(entry.timestamp)}</div>
                      
                      <div className="entry-main">
                        <div className="entry-single-row">
                          <div className="entry-left">
                            <span className="entry-title">⚗️ Purified {entry.objectCount} object{entry.objectCount !== 1 ? 's' : ''}</span>
                            <div className="category-pills">
                              {entry.categoryBreakdown?.TOXIC > 0 && (
                                <span className="category-pill toxic">{CATEGORIES.TOXIC.icon} {entry.categoryBreakdown.TOXIC}</span>
                              )}
                              {entry.categoryBreakdown?.SPAM > 0 && (
                                <span className="category-pill spam">{CATEGORIES.SPAM.icon} {entry.categoryBreakdown.SPAM}</span>
                              )}
                              {entry.categoryBreakdown?.DUST > 0 && (
                                <span className="category-pill dust">{CATEGORIES.DUST.icon} {entry.categoryBreakdown.DUST}</span>
                              )}
                            </div>
                          </div>
                          <div className="entry-right">
                            <span className="reward-item">💎 +{entry.purityEarned} pts</span>
                            <span className="reward-item" title="Storage rebate reclaimed (1 SUI = 1,000,000,000 MIST)">💰 +{
                              (() => {
                                const mist = entry.storageRebateMist
                                  ? Math.round(entry.storageRebateMist)
                                  : Math.round((entry.suiReclaimed || 0) * 1_000_000_000);
                                return mist > 0 ? mist.toLocaleString() + ' MIST' : '0 MIST';
                              })()
                            }</span>
                            <span className="entry-expand">{isExpanded ? '▼' : '▶'}</span>
                          </div>
                        </div>
                        {entry.tierUpOccurred && (
                          <div className="tier-up-badge">🎉 Tier Up! → {entry.newTierName}</div>
                        )}
                      </div>
                    </div>

                    {/* Expanded Details */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          className="history-entry-details"
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                        >
                          {/* Objects List by Category */}
                          {entry.objectsPurified && entry.objectsPurified.length > 0 && (
                            <div className="objects-section">
                              <h4>Objects Purified:</h4>
                              
                              {/* Toxic Objects */}
                              {entry.objectsPurified.filter(obj => obj.category === 'TOXIC').length > 0 && (
                                <div className="category-group">
                                  <div className="category-group-header toxic">
                                    {CATEGORIES.TOXIC.icon} TOXIC ({entry.objectsPurified.filter(obj => obj.category === 'TOXIC').length})
                                  </div>
                                  {entry.objectsPurified
                                    .filter(obj => obj.category === 'TOXIC')
                                    .map((obj, i) => (
                                      <div key={i} className="object-detail">
                                        <span className="object-name">{obj.name}</span>
                                        <span className="object-reason">{obj.reason}</span>
                                      </div>
                                    ))}
                                </div>
                              )}

                              {/* Spam Objects */}
                              {entry.objectsPurified.filter(obj => obj.category === 'SPAM').length > 0 && (
                                <div className="category-group">
                                  <div className="category-group-header spam">
                                    {CATEGORIES.SPAM.icon} SPAM ({entry.objectsPurified.filter(obj => obj.category === 'SPAM').length})
                                  </div>
                                  {entry.objectsPurified
                                    .filter(obj => obj.category === 'SPAM')
                                    .map((obj, i) => (
                                      <div key={i} className="object-detail">
                                        <span className="object-name">{obj.name}</span>
                                        <span className="object-reason">{obj.reason}</span>
                                      </div>
                                    ))}
                                </div>
                              )}

                              {/* Dust Objects */}
                              {entry.objectsPurified.filter(obj => obj.category === 'DUST').length > 0 && (
                                <div className="category-group">
                                  <div className="category-group-header dust">
                                    {CATEGORIES.DUST.icon} DUST ({entry.objectsPurified.filter(obj => obj.category === 'DUST').length})
                                  </div>
                                  {entry.objectsPurified
                                    .filter(obj => obj.category === 'DUST')
                                    .map((obj, i) => (
                                      <div key={i} className="object-detail">
                                        <span className="object-name">{obj.name}</span>
                                        <span className="object-reason">{obj.reason}</span>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Transaction Info */}
                          {entry.transactionHash && (
                            <div className="transaction-section">
                              <h4>Transaction:</h4>
                              <div className="transaction-detail">
                                <span className="tx-hash">
                                  {entry.transactionHash.slice(0, 8)}...{entry.transactionHash.slice(-6)}
                                </span>
                                <a 
                                  href={`https://suivision.xyz/txblock/${entry.transactionHash}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="explorer-link"
                                >
                                  View Tx
                                </a>
                              </div>
                              {entry.gasUsed > 0 && (
                                <div className="gas-info">
                                  ⛽ Gas: {entry.gasUsed.toFixed(6)} SUI
                                </div>
                              )}
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                </React.Fragment>
              );
            })
          )}
        </div>

        {/* Category Breakdown at Bottom */}
        {stats && filteredHistory.length > 0 && stats.totalObjectsCleaned > 0 && (
          <div className="category-breakdown">
            <h3>Category Breakdown</h3>
            <div className="breakdown-stats">
              {stats.categoryBreakdown.TOXIC > 0 && (
                <div className="breakdown-item toxic">
                  <div className="breakdown-icon">{CATEGORIES.TOXIC.icon}</div>
                  <div className="breakdown-info">
                    <div className="breakdown-value">{stats.categoryBreakdown.TOXIC}</div>
                    <div className="breakdown-label">Toxic</div>
                  </div>
                  <div className="breakdown-percent">
                    {((stats.categoryBreakdown.TOXIC / stats.totalObjectsCleaned) * 100).toFixed(0)}%
                  </div>
                </div>
              )}
              {stats.categoryBreakdown.SPAM > 0 && (
                <div className="breakdown-item spam">
                  <div className="breakdown-icon">{CATEGORIES.SPAM.icon}</div>
                  <div className="breakdown-info">
                    <div className="breakdown-value">{stats.categoryBreakdown.SPAM}</div>
                    <div className="breakdown-label">Spam</div>
                  </div>
                  <div className="breakdown-percent">
                    {((stats.categoryBreakdown.SPAM / stats.totalObjectsCleaned) * 100).toFixed(0)}%
                  </div>
                </div>
              )}
              {stats.categoryBreakdown.DUST > 0 && (
                <div className="breakdown-item dust">
                  <div className="breakdown-icon">{CATEGORIES.DUST.icon}</div>
                  <div className="breakdown-info">
                    <div className="breakdown-value">{stats.categoryBreakdown.DUST}</div>
                    <div className="breakdown-label">Dust</div>
                  </div>
                  <div className="breakdown-percent">
                    {((stats.categoryBreakdown.DUST / stats.totalObjectsCleaned) * 100).toFixed(0)}%
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default HistoryPanel;
